import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Icon } from '../components/Icons';
import CountryFlag from '../components/CountryFlag';
import '../styles/Signup.css';

/* ── same icons as login ── */
const GoogleIcon = () => (
  <svg style={{ width:17, height:17 }} viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
    <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);
const MicrosoftIcon = () => (
  <div style={{ width:15, height:15, display:'grid', gridTemplateColumns:'1fr 1fr', gap:2 }}>
    <span style={{ background:'#F25022', borderRadius:1 }}/>
    <span style={{ background:'#7FBA00', borderRadius:1 }}/>
    <span style={{ background:'#00A4EF', borderRadius:1 }}/>
    <span style={{ background:'#FFB900', borderRadius:1 }}/>
  </div>
);

/* ── SVG illustration — clipboard + clock + plant + paper plane (matches screenshot) ── */
const SignupIllustration = () => (
  <svg viewBox="0 0 480 240" xmlns="http://www.w3.org/2000/svg"
    style={{ width:'100%', maxWidth:480, height:'auto' }} aria-hidden="true">

    {/* bar charts right-back */}
    <rect x="390" y="140" width="18" height="80" rx="4" fill="#93C5FD" opacity="0.75"/>
    <rect x="414" y="155" width="18" height="65" rx="4" fill="#BFDBFE" opacity="0.7"/>
    <rect x="438" y="130" width="18" height="90" rx="4" fill="#7DC3FA" opacity="0.8"/>

    {/* main card / clipboard */}
    <rect x="120" y="30" width="220" height="180" rx="16"
      fill="white" opacity="0.85"
      style={{ filter:'drop-shadow(0 6px 24px rgba(37,99,235,0.14))' }}/>
    {/* clipboard clip */}
    <rect x="198" y="22" width="64" height="18" rx="9" fill="#BFDBFE"/>
    <rect x="208" y="26" width="44" height="10" rx="5" fill="#93C5FD" opacity="0.8"/>

    {/* lines on clipboard */}
    <rect x="140" y="68"  width="140" height="8"  rx="4" fill="#DBEAFE"/>
    <rect x="140" y="84"  width="110" height="6"  rx="3" fill="#EFF6FF"/>
    <rect x="140" y="98"  width="125" height="6"  rx="3" fill="#EFF6FF"/>

    {/* check rows */}
    <circle cx="150" cy="126" r="10" fill="#BFDBFE"/>
    <polyline points="145,126 149,130 156,122" stroke="#2563EB" strokeWidth="2.2" fill="none" strokeLinecap="round"/>
    <rect x="166" y="122" width="80" height="6"  rx="3" fill="#DBEAFE"/>
    <rect x="166" y="131" width="60" height="4"  rx="2" fill="#EFF6FF"/>

    <circle cx="150" cy="155" r="10" fill="#BFDBFE"/>
    <polyline points="145,155 149,159 156,151" stroke="#2563EB" strokeWidth="2.2" fill="none" strokeLinecap="round"/>
    <rect x="166" y="151" width="72" height="6"  rx="3" fill="#DBEAFE"/>
    <rect x="166" y="160" width="54" height="4"  rx="2" fill="#EFF6FF"/>

    {/* clock bottom-right */}
    <circle cx="318" cy="175" r="38" fill="#DBEAFE"
      style={{ filter:'drop-shadow(0 4px 14px rgba(37,99,235,0.15))' }}/>
    <circle cx="318" cy="175" r="30" fill="white" opacity="0.9"/>
    <circle cx="318" cy="175" r="3"  fill="#2563EB"/>
    {/* clock hands */}
    <line x1="318" y1="175" x2="318" y2="153" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="318" y1="175" x2="334" y2="182" stroke="#93C5FD" strokeWidth="2"   strokeLinecap="round"/>
    {/* tick marks */}
    <line x1="318" y1="148" x2="318" y2="152" stroke="#BFDBFE" strokeWidth="2"/>
    <line x1="318" y1="196" x2="318" y2="200" stroke="#BFDBFE" strokeWidth="2"/>
    <line x1="293" y1="175" x2="297" y2="175" stroke="#BFDBFE" strokeWidth="2"/>
    <line x1="339" y1="175" x2="343" y2="175" stroke="#BFDBFE" strokeWidth="2"/>

    {/* paper plane */}
    <polygon points="340,30 380,50 345,62" fill="#60A5FA" opacity="0.85"/>
    <polygon points="340,30 345,62 328,48" fill="#93C5FD" opacity="0.7"/>
    <line x1="340" y1="30" x2="348" y2="56" stroke="#BFDBFE" strokeWidth="1.5" opacity="0.6"/>
    {/* dotted trail */}
    <circle cx="360" cy="72" r="3" fill="#BFDBFE" opacity="0.6"/>
    <circle cx="370" cy="84" r="2" fill="#BFDBFE" opacity="0.45"/>
    <circle cx="376" cy="96" r="1.5" fill="#BFDBFE" opacity="0.35"/>

    {/* plant bottom-left */}
    <path d="M72,240 L58,218 L86,218 Z" fill="#BFDBFE" opacity="0.8"/>
    <rect x="61" y="226" width="22" height="14" rx="3" fill="#93C5FD" opacity="0.7"/>
    <line x1="72" y1="218" x2="72" y2="192" stroke="#7DC3FA" strokeWidth="3" strokeLinecap="round"/>
    <ellipse cx="60" cy="200" rx="13" ry="8" fill="#93C5FD" opacity="0.8" transform="rotate(-28,60,200)"/>
    <ellipse cx="84" cy="196" rx="13" ry="8" fill="#BFDBFE" opacity="0.8" transform="rotate(28,84,196)"/>
    <ellipse cx="65" cy="210" rx="10" ry="6" fill="#BFDBFE" opacity="0.65" transform="rotate(-12,65,210)"/>

    {/* floating dots decoration */}
    <circle cx="420" cy="40"  r="6" fill="#BFDBFE" opacity="0.6"/>
    <circle cx="438" cy="28"  r="3.5" fill="#93C5FD" opacity="0.5"/>
    <circle cx="405" cy="32"  r="4" fill="#DBEAFE" opacity="0.55"/>
  </svg>
);

const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "United States (America/New_York)", country: "US" },
  { value: "Asia/Kolkata",     label: "India (Asia/Kolkata)",             country: "IN" },
];

const FEATURES = [
  { cls:'blue',   iconColor:'#2563EB', icon:'FileText',    title:'Smart Timesheets',   desc:'Easy logging with auto time tracking' },
  { cls:'green',  iconColor:'#10B981', icon:'CheckCircle', title:'Instant Approvals',  desc:'Real-time notifications and quick approvals' },
  { cls:'purple', iconColor:'#8B5CF6', icon:'BarChart',    title:'Detailed Reports',   desc:'Insights to boost your productivity' },
  { cls:'indigo', iconColor:'#4F46E5', icon:'Lock',        title:'Secure & Reliable',  desc:'Your data is safe with us' },
];

const EMAIL_REGEX = /^[^\s@]{1,64}@[^\s@]{1,255}$/;

const validateName = (v) => (!v.trim() ? 'Full name is required' : null);

const validateEmail = (v) => {
  if (!v.trim()) return 'Email is required';
  if (!EMAIL_REGEX.test(v)) return 'Enter a valid email address';
  return null;
};

const validateUsername = (v) => {
  if (!v.trim()) return 'Username is required';
  if (v.length < 3) return 'Username must be at least 3 characters';
  if (/\s/.test(v)) return 'Username cannot contain spaces';
  return null;
};

const validateMobile = (v) => {
  if (!v.trim()) return 'Mobile number is required';
  if (!/^\d{10}$/.test(v)) return 'Enter a valid 10-digit number';
  return null;
};

const validatePassword = (v, valid) => {
  if (!v.trim()) return 'Password is required';
  if (!valid) return 'Password does not meet criteria';
  return null;
};

const validateConfirm = (v, pwd) => {
  if (!v.trim()) return 'Please confirm your password';
  if (v !== pwd) return 'Passwords do not match';
  return null;
};

const validateForm = (form, isPasswordValid, isEmployeeCode) => {
  const errs = {};
  const fields = {
    name:             validateName(form.name),
    email:            validateEmail(form.email),
    username:         validateUsername(form.username),
    mobilenumber:     validateMobile(form.mobilenumber),
    password:         validatePassword(form.password, isPasswordValid),
    confirmPassword:  validateConfirm(form.confirmPassword, form.password),
    registrationcode: !form.registrationcode.trim() ? 'Registration code is required' : null,
  };
  Object.entries(fields).forEach(([k, v]) => { if (v) errs[k] = v; });
  if (isEmployeeCode && !form.startDate) errs.startDate = 'Start date is required for employees';
  return errs;
};

const SignupForm = ({ form, errors, passwordValid, showPassword, showConfirm, tzOpen, tzRef, isEmployeeCode, agreedTerms, serverMessage, handleChange, setShowPassword, setShowConfirm, setTzOpen, setForm, setAgreedTerms, handleSubmit }) => (
  <motion.div className="signup-card"
    initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }}
    transition={{ duration:0.42, ease:[0.4,0,0.2,1] }}>
    <div className="signup-card-header">
      <h2>Create Your Account</h2>
      <p>Join SKyPA Systems and get started with your workforce management journey.</p>
    </div>
    <div className="signup-fields-grid">
      <div className="form-group">
        <label className="field-label" htmlFor="su-name">Full Name</label>
        <div className="input-wrap">
          <span className="input-icon"><Icon.User size={14} color="#94A3B8"/></span>
          <input id="su-name" name="name" type="text" className={`input-field ${errors.name?'input-error':''}`} placeholder="Enter your full name" value={form.name} onChange={handleChange}/>
        </div>
        {errors.name && <span className="error-text"><Icon.AlertCircle size={12} color="#EF4444"/> {errors.name}</span>}
      </div>
      <div className="form-group">
        <label className="field-label" htmlFor="su-email">Email Address</label>
        <div className="input-wrap">
          <span className="input-icon"><Icon.Mail size={14} color="#94A3B8"/></span>
          <input id="su-email" name="email" type="email" className={`input-field ${errors.email?'input-error':''}`} placeholder="Enter your email address" value={form.email} onChange={handleChange}/>
        </div>
        {errors.email && <span className="error-text"><Icon.AlertCircle size={12} color="#EF4444"/> {errors.email}</span>}
      </div>
      <div className="form-group">
        <label className="field-label" htmlFor="su-password">Password</label>
        <div className="input-wrap">
          <span className="input-icon"><Icon.Lock size={14} color="#94A3B8"/></span>
          <input id="su-password" name="password" type={showPassword?'text':'password'} className={`input-field ${errors.password?'input-error':''}`} placeholder="Create a password" value={form.password} onChange={handleChange}/>
          <button type="button" className="su-eye-btn" onClick={() => setShowPassword(v=>!v)} aria-label={showPassword?'Hide':'Show'}>
            {showPassword ? <Icon.EyeOff size={14} color="#94A3B8"/> : <Icon.Eye size={14} color="#94A3B8"/>}
          </button>
        </div>
        {errors.password && <span className="error-text"><Icon.AlertCircle size={12} color="#EF4444"/> {errors.password}</span>}
      </div>
      <div className="form-group">
        <label className="field-label" htmlFor="su-confirm">Confirm Password</label>
        <div className="input-wrap">
          <span className="input-icon"><Icon.Lock size={14} color="#94A3B8"/></span>
          <input id="su-confirm" name="confirmPassword" type={showConfirm?'text':'password'} className={`input-field ${errors.confirmPassword?'input-error':''}`} placeholder="Confirm your password" value={form.confirmPassword} onChange={handleChange}/>
          <button type="button" className="su-eye-btn" onClick={() => setShowConfirm(v=>!v)} aria-label={showConfirm?'Hide':'Show'}>
            {showConfirm ? <Icon.EyeOff size={14} color="#94A3B8"/> : <Icon.Eye size={14} color="#94A3B8"/>}
          </button>
        </div>
        {errors.confirmPassword && <span className="error-text"><Icon.AlertCircle size={12} color="#EF4444"/> {errors.confirmPassword}</span>}
      </div>
      <ul className="password-criteria">
        <li className={passwordValid.length?'valid':'invalid'}>8+ chars</li>
        <li className={passwordValid.uppercase?'valid':'invalid'}>Uppercase</li>
        <li className={passwordValid.lowercase?'valid':'invalid'}>Lowercase</li>
        <li className={passwordValid.number?'valid':'invalid'}>Number</li>
        <li className={passwordValid.specialChar?'valid':'invalid'}>Special char</li>
      </ul>
      <div className="signup-two-col">
        <div className="form-group">
          <label className="field-label" htmlFor="su-username">Username</label>
          <div className="input-wrap">
            <span className="input-icon"><Icon.Badge size={14} color="#94A3B8"/></span>
            <input id="su-username" name="username" type="text" className={`input-field ${errors.username?'input-error':''}`} placeholder="johndoe" value={form.username} onChange={handleChange}/>
          </div>
          {errors.username && <span className="error-text">{errors.username}</span>}
        </div>
        <div className="form-group">
          <label className="field-label" htmlFor="su-mobile">Mobile</label>
          <div className="input-wrap">
            <span className="input-icon"><Icon.Phone size={14} color="#94A3B8"/></span>
            <input id="su-mobile" name="mobilenumber" type="tel" className={`input-field ${errors.mobilenumber?'input-error':''}`} placeholder="10-digit number" value={form.mobilenumber} onChange={handleChange}/>
          </div>
          {errors.mobilenumber && <span className="error-text">{errors.mobilenumber}</span>}
        </div>
      </div>
      <div className="form-group">
        <label className="field-label" htmlFor="su-regcode">Registration Code</label>
        <div className="input-wrap">
          <span className="input-icon"><Icon.Key size={14} color="#94A3B8"/></span>
          <input id="su-regcode" name="registrationcode" type="text" className={`input-field ${errors.registrationcode?'input-error':''}`} placeholder="e.g. EE-AZXNBM" value={form.registrationcode} onChange={handleChange}/>
        </div>
        {errors.registrationcode && <span className="error-text">{errors.registrationcode}</span>}
      </div>
      <div className="form-group">
        <label className="field-label" htmlFor="su-tz">Timezone</label>
        <div ref={tzRef} style={{ position:'relative' }}>
          <button id="su-tz" type="button" className="input-field tz-trigger" onClick={() => setTzOpen(o=>!o)}>
            <CountryFlag countryCode={TIMEZONE_OPTIONS.find(o=>o.value===form.timezone)?.country||'US'} size={16}/>
            <span style={{ flex:1, fontSize:'0.84rem', color:'#0F172A' }}>{TIMEZONE_OPTIONS.find(o=>o.value===form.timezone)?.label}</span>
            <Icon.ChevronDown size={13} color="#94A3B8" style={{ flexShrink:0, transition:'transform 0.2s', transform:tzOpen?'rotate(180deg)':'rotate(0deg)' }}/>
          </button>
          {tzOpen && (
            <div className="tz-dropdown-panel">
              {TIMEZONE_OPTIONS.map(opt => (
                <button key={opt.value} type="button" className={`tz-option ${form.timezone===opt.value?'tz-option-selected':''}`}
                  onClick={() => { setForm(p=>({...p,timezone:opt.value})); setTzOpen(false); }}>
                  <CountryFlag countryCode={opt.country} size={16}/>
                  <span>{opt.label}</span>
                  {form.timezone===opt.value && <Icon.Check size={13} color="#2563EB" style={{ marginLeft:'auto' }}/>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {isEmployeeCode && (
        <div className="form-group">
          <label className="field-label" htmlFor="su-startDate">Start / Joining Date</label>
          <div className="input-wrap">
            <span className="input-icon"><Icon.Calendar size={14} color="#94A3B8"/></span>
            <input id="su-startDate" name="startDate" type="date" className={`input-field ${errors.startDate?'input-error':''}`} value={form.startDate} onChange={handleChange} max={new Date().toISOString().split('T')[0]}/>
          </div>
          {errors.startDate && <span className="error-text">{errors.startDate}</span>}
        </div>
      )}
    </div>
    <label className="su-terms">
      <input type="checkbox" checked={agreedTerms} onChange={e => setAgreedTerms(e.target.checked)} />
      {" "}I agree to the <a href="#terms">Terms of Service</a> and <a href="#privacy">Privacy Policy</a>
    </label>
    {errors.terms && <span className="error-text"><Icon.AlertCircle size={12} color="#EF4444"/> {errors.terms}</span>}
    {serverMessage.text && (
      <div className={`server-message ${serverMessage.type==='success'?'server-success':'server-error'}`}>
        {serverMessage.type==='success' ? <Icon.CheckCircle size={14} color="#10B981"/> : <Icon.AlertCircle size={14} color="#EF4444"/>}
        {serverMessage.text}
      </div>
    )}
    <motion.button type="button" className="button-signup" onClick={handleSubmit} whileHover={{ scale:1.01 }} whileTap={{ scale:0.99 }}>
      Create Account <Icon.ArrowRight size={16} color="#fff"/>
    </motion.button>
    <div className="su-signin-link">
      Already have an account?{' '}
      <Link to="/login" className="auth-link-inline">Sign in</Link>
    </div>
  </motion.div>
);

const Signup = () => {
  const [form, setForm] = useState({
    name: '', email: '', username: '', password: '', confirmPassword: '',
    mobilenumber: '', registrationcode: '', startDate: '', timezone: 'America/New_York',
  });
  const [showPassword,    setShowPassword]    = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [passwordValid,   setPasswordValid]   = useState({ length:false, uppercase:false, lowercase:false, number:false, specialChar:false });
  const [errors,          setErrors]          = useState({});
  const [serverMessage,   setServerMessage]   = useState({ text:'', type:'' });
  const [tzOpen,          setTzOpen]          = useState(false);
  const [agreedTerms,     setAgreedTerms]     = useState(false);
  const tzRef    = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e) => { if (tzRef.current && !tzRef.current.contains(e.target)) setTzOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const isEmployeeCode = form.registrationcode.toUpperCase().startsWith('EE-');

  const checkPasswordStrength = (value) => ({
    length:      value.length >= 8,
    uppercase:   /[A-Z]/.test(value),
    lowercase:   /[a-z]/.test(value),
    number:      /\d/.test(value),
    specialChar: /[!@#$%^&*(),.:?{}|<>]/.test(value),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
    if (name === 'password') setPasswordValid(checkPasswordStrength(value));
  };

  const isPasswordValid = Object.values(passwordValid).every(Boolean);

  const submitRegistration = async () => {
    const res = await fetch('/timesheets/registration', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, confirmPassword: undefined, startDate: isEmployeeCode && form.startDate ? form.startDate : undefined }),
    });
    const txt = await res.text();
    if (res.ok) {
      setServerMessage({ text: txt, type: 'success' });
      setTimeout(() => navigate('/login'), 1500);
    } else {
      setServerMessage({ text: txt, type: 'error' });
    }
  };

  const handleSubmit = async () => {
    const errs = validateForm(form, isPasswordValid, isEmployeeCode);
    if (!agreedTerms) errs.terms = 'You must agree to the Terms of Service';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setServerMessage({ text:'', type:'' });
    try {
      await submitRegistration();
    } catch {
      setServerMessage({ text: 'Signup failed. Please try again.', type: 'error' });
    }
  };

  return (
    <div className="signup-page">

      {/* ══ LEFT ══ */}
      <motion.div className="signup-left"
        initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ duration:0.45 }}>
        <div className="signup-dot-grid-tr" aria-hidden="true"/>
        <div className="signup-dot-grid-bl" aria-hidden="true"/>

        <div className="signup-left-content">
          <div className="signup-brand">
            <span className="signup-tag">WorkForce Management</span>
            <motion.h1
              initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
              transition={{ delay:0.1, duration:0.4 }}>
              Streamline Your<br/>Workforce Today
            </motion.h1>
            <motion.p
              initial={{ opacity:0 }} animate={{ opacity:1 }}
              transition={{ delay:0.2, duration:0.38 }}>
              Join SKyPA Systems and simplify timesheet tracking,<br/>
              approvals, and workforce management — all in one<br/>secure platform.
            </motion.p>
          </div>

          <div className="signup-features">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} className="signup-feature-item"
                initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }}
                transition={{ delay:0.28+i*0.08, duration:0.36 }}>
                <div className={`su-icon ${f.cls}`}>
                  {f.icon==='FileText'    && <Icon.FileText    size={17} color={f.iconColor}/>}
                  {f.icon==='CheckCircle' && <Icon.CheckCircle size={17} color={f.iconColor}/>}
                  {f.icon==='BarChart'    && <Icon.BarChart    size={17} color={f.iconColor}/>}
                  {f.icon==='Lock'        && <Icon.Lock        size={17} color={f.iconColor}/>}
                </div>
                <div className="su-text">
                  <h4>{f.title}</h4>
                  <p>{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="signup-illustration">
          <SignupIllustration/>
        </div>
      </motion.div>

      {/* ══ RIGHT CARD ══ */}
      <div className="signup-right">
        <motion.div className="signup-card"
          initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }}
          transition={{ duration:0.42, ease:[0.4,0,0.2,1] }}>

          <div className="signup-card-header">
            <h2>Create Your Account</h2>
            <p>Join SKyPA Systems and get started with your workforce management journey.</p>
          </div>

          <div className="signup-fields-grid">
            {/* Full Name */}
            <div className="form-group">
              <label className="field-label" htmlFor="su-name">Full Name</label>
              <div className="input-wrap">
                <span className="input-icon"><Icon.User size={14} color="#94A3B8"/></span>
                <input id="su-name" name="name" type="text"
                  className={`input-field ${errors.name?'input-error':''}`}
                  placeholder="Enter your full name"
                  value={form.name} onChange={handleChange}/>
              </div>
              {errors.name && <span className="error-text"><Icon.AlertCircle size={12} color="#EF4444"/> {errors.name}</span>}
            </div>

            {/* Email */}
            <div className="form-group">
              <label className="field-label" htmlFor="su-email">Email Address</label>
              <div className="input-wrap">
                <span className="input-icon"><Icon.Mail size={14} color="#94A3B8"/></span>
                <input id="su-email" name="email" type="email"
                  className={`input-field ${errors.email?'input-error':''}`}
                  placeholder="Enter your email address"
                  value={form.email} onChange={handleChange}/>
              </div>
              {errors.email && <span className="error-text"><Icon.AlertCircle size={12} color="#EF4444"/> {errors.email}</span>}
            </div>

            {/* Password */}
            <div className="form-group">
              <label className="field-label" htmlFor="su-password">Password</label>
              <div className="input-wrap">
                <span className="input-icon"><Icon.Lock size={14} color="#94A3B8"/></span>
                <input id="su-password" name="password" type={showPassword?'text':'password'}
                  className={`input-field ${errors.password?'input-error':''}`}
                  placeholder="Create a password"
                  value={form.password} onChange={handleChange}/>
                <button type="button" className="su-eye-btn" onClick={() => setShowPassword(v=>!v)}
                  aria-label={showPassword?'Hide':'Show'}>
                  {showPassword ? <Icon.EyeOff size={14} color="#94A3B8"/> : <Icon.Eye size={14} color="#94A3B8"/>}
                </button>
              </div>
              {errors.password && <span className="error-text"><Icon.AlertCircle size={12} color="#EF4444"/> {errors.password}</span>}
            </div>

            {/* Confirm Password */}
            <div className="form-group">
              <label className="field-label" htmlFor="su-confirm">Confirm Password</label>
              <div className="input-wrap">
                <span className="input-icon"><Icon.Lock size={14} color="#94A3B8"/></span>
                <input id="su-confirm" name="confirmPassword" type={showConfirm?'text':'password'}
                  className={`input-field ${errors.confirmPassword?'input-error':''}`}
                  placeholder="Confirm your password"
                  value={form.confirmPassword} onChange={handleChange}/>
                <button type="button" className="su-eye-btn" onClick={() => setShowConfirm(v=>!v)}
                  aria-label={showConfirm?'Hide':'Show'}>
                  {showConfirm ? <Icon.EyeOff size={14} color="#94A3B8"/> : <Icon.Eye size={14} color="#94A3B8"/>}
                </button>
              </div>
              {errors.confirmPassword && <span className="error-text"><Icon.AlertCircle size={12} color="#EF4444"/> {errors.confirmPassword}</span>}
            </div>

            {/* Password criteria */}
            <ul className="password-criteria">
              <li className={passwordValid.length?'valid':'invalid'}>8+ chars</li>
              <li className={passwordValid.uppercase?'valid':'invalid'}>Uppercase</li>
              <li className={passwordValid.lowercase?'valid':'invalid'}>Lowercase</li>
              <li className={passwordValid.number?'valid':'invalid'}>Number</li>
              <li className={passwordValid.specialChar?'valid':'invalid'}>Special char</li>
            </ul>

            {/* Username + Mobile */}
            <div className="signup-two-col">
              <div className="form-group">
                <label className="field-label" htmlFor="su-username">Username</label>
                <div className="input-wrap">
                  <span className="input-icon"><Icon.Badge size={14} color="#94A3B8"/></span>
                  <input id="su-username" name="username" type="text"
                    className={`input-field ${errors.username?'input-error':''}`}
                    placeholder="johndoe"
                    value={form.username} onChange={handleChange}/>
                </div>
                {errors.username && <span className="error-text">{errors.username}</span>}
              </div>
              <div className="form-group">
                <label className="field-label" htmlFor="su-mobile">Mobile</label>
                <div className="input-wrap">
                  <span className="input-icon"><Icon.Phone size={14} color="#94A3B8"/></span>
                  <input id="su-mobile" name="mobilenumber" type="tel"
                    className={`input-field ${errors.mobilenumber?'input-error':''}`}
                    placeholder="10-digit number"
                    value={form.mobilenumber} onChange={handleChange}/>
                </div>
                {errors.mobilenumber && <span className="error-text">{errors.mobilenumber}</span>}
              </div>
            </div>

            {/* Registration code */}
            <div className="form-group">
              <label className="field-label" htmlFor="su-regcode">Registration Code</label>
              <div className="input-wrap">
                <span className="input-icon"><Icon.Key size={14} color="#94A3B8"/></span>
                <input id="su-regcode" name="registrationcode" type="text"
                  className={`input-field ${errors.registrationcode?'input-error':''}`}
                  placeholder="e.g. EE-AZXNBM"
                  value={form.registrationcode} onChange={handleChange}/>
              </div>
              {errors.registrationcode && <span className="error-text">{errors.registrationcode}</span>}
            </div>

            {/* Timezone */}
            <div className="form-group">
              <label className="field-label" htmlFor="su-tz">Timezone</label>
              <div ref={tzRef} style={{ position:'relative' }}>
                <button id="su-tz" type="button" className="input-field tz-trigger"
                  onClick={() => setTzOpen(o=>!o)}>
                  <CountryFlag countryCode={TIMEZONE_OPTIONS.find(o=>o.value===form.timezone)?.country||'US'} size={16}/>
                  <span style={{ flex:1, fontSize:'0.84rem', color:'#0F172A' }}>
                    {TIMEZONE_OPTIONS.find(o=>o.value===form.timezone)?.label}
                  </span>
                  <Icon.ChevronDown size={13} color="#94A3B8"
                    style={{ flexShrink:0, transition:'transform 0.2s', transform:tzOpen?'rotate(180deg)':'rotate(0deg)' }}/>
                </button>
                {tzOpen && (
                  <div className="tz-dropdown-panel">
                    {TIMEZONE_OPTIONS.map(opt => (
                      <button key={opt.value} type="button"
                        className={`tz-option ${form.timezone===opt.value?'tz-option-selected':''}`}
                        onClick={() => { setForm(p=>({...p,timezone:opt.value})); setTzOpen(false); }}>
                        <CountryFlag countryCode={opt.country} size={16}/>
                        <span>{opt.label}</span>
                        {form.timezone===opt.value && <Icon.Check size={13} color="#2563EB" style={{ marginLeft:'auto' }}/>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Start date (employee only) */}
            {isEmployeeCode && (
              <div className="form-group">
                <label className="field-label" htmlFor="su-startDate">Start / Joining Date</label>
                <div className="input-wrap">
                  <span className="input-icon"><Icon.Calendar size={14} color="#94A3B8"/></span>
                  <input id="su-startDate" name="startDate" type="date"
                    className={`input-field ${errors.startDate?'input-error':''}`}
                    value={form.startDate} onChange={handleChange}
                    max={new Date().toISOString().split('T')[0]}/>
                </div>
                {errors.startDate && <span className="error-text">{errors.startDate}</span>}
              </div>
            )}
          </div>

          {/* Terms */}
          <label className="su-terms">
            <input type="checkbox" checked={agreedTerms} onChange={e => setAgreedTerms(e.target.checked)} />
            {" "}I agree to the <a href="#terms">Terms of Service</a> and <a href="#privacy">Privacy Policy</a>
          </label>
          {errors.terms && <span className="error-text"><Icon.AlertCircle size={12} color="#EF4444"/> {errors.terms}</span>}

          {/* Server message */}
          {serverMessage.text && (
            <div className={`server-message ${serverMessage.type==='success'?'server-success':'server-error'}`}>
              {serverMessage.type==='success'
                ? <Icon.CheckCircle size={14} color="#10B981"/>
                : <Icon.AlertCircle size={14} color="#EF4444"/>}
              {serverMessage.text}
            </div>
          )}

          {/* Create account button */}
          <motion.button type="button" className="button-signup"
            onClick={handleSubmit}
            whileHover={{ scale:1.01 }} whileTap={{ scale:0.99 }}>
            Create Account <Icon.ArrowRight size={16} color="#fff"/>
          </motion.button>



          <div className="su-signin-link">
            Already have an account?{' '}
            <Link to="/login" className="auth-link-inline">Sign in</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Signup;
